import React, { useEffect, useState } from 'react';
import { Form, Input, Alert } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import CustomModal from '@/common/CustomModal';
import FormItem from '@/common/FormItem';

const makeFieldName = () => `pcm_${Math.random().toString(36).slice(2, 10)}`;

const PasswordConfirmModal = ({
  visible,
  title = 'Confirm with your password',
  description = 'Please re-enter your password to save these changes.',
  okText = 'Confirm',
  cancelText = 'Cancel',
  errorMessage = '',
  loading = false,
  onConfirm,
  onCancel
}) => {
  const [form] = Form.useForm();

  // Random field name on each open defeats browser/password-manager autofill that keys off field name
  const [fieldName, setFieldName] = useState(makeFieldName);

  // Chrome ignores autoComplete hints but will not autofill an input that is readOnly at mount.
  // We start the field readOnly and remove the flag after first user interaction.
  const [isReadOnly, setIsReadOnly] = useState(true);

  useEffect(() => {
    if (visible) {
      setFieldName(makeFieldName());
      form.resetFields();
      setIsReadOnly(true);
    }
  }, [visible, form]);

  // Clear the password field whenever a server-side error is shown,
  // so the admin must retype rather than re-submitting a stale value.
  useEffect(() => {
    if (errorMessage) {
      form.setFieldsValue({ [fieldName]: '' });
      setIsReadOnly(true);
    }
  }, [errorMessage, form, fieldName]);

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const password = values[fieldName];
        onConfirm && onConfirm(password);
      })
      .catch(() => {
        // antd shows the field-level error itself
      });
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel && onCancel();
  };

  return (
    <CustomModal
      title={title}
      visible={visible}
      onCancel={handleCancel}
      maskClosable={false}
      destroyOnClose={true}
      width={420}
      okText={okText}
      cancelText={cancelText}
      okButtonProps={{
        onClick: handleOk,
        loading,
        htmlType: 'submit'
      }}
      cancelButtonProps={{
        onClick: handleCancel,
        disabled: loading
      }}
    >
      {/* Hidden decoy fields trap aggressive autofill so the real field stays empty */}
      <div
        style={{
          position: 'absolute',
          top: '-1000px',
          left: '-1000px',
          height: 0,
          width: 0,
          opacity: 0,
          overflow: 'hidden'
        }}
        aria-hidden="true"
      >
        <input
          type="text"
          name="username"
          autoComplete="username"
          tabIndex={-1}
        />
        <input
          type="password"
          name="password"
          autoComplete="current-password"
          tabIndex={-1}
        />
      </div>

      <p style={{ marginBottom: 16 }}>{description}</p>

      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        onFinish={handleOk}
      >
        <FormItem
          label="Your Password"
          name={fieldName}
          rules={[{ required: true, message: 'Please enter your password.' }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter your password"
            autoComplete="new-password"
            autoFocus
            readOnly={isReadOnly}
            onFocus={() => setIsReadOnly(false)}
            data-lpignore="true"
            data-form-type="other"
            data-1p-ignore="true"
            visibilityToggle={false}
            onPressEnter={handleOk}
          />
        </FormItem>

        {errorMessage ? (
          <Alert
            type="error"
            showIcon
            message={errorMessage}
            style={{ marginTop: 8 }}
          />
        ) : null}
      </Form>
    </CustomModal>
  );
};

export default PasswordConfirmModal;
